import { create } from 'zustand';

import { api } from '../lib/api';

/**
 * Playback state (HANDOFF §9.1).
 *
 * The store holds intent and state; `PlayerHost` owns the single `<audio>`
 * element and reconciles it against this. That split is what keeps the element
 * out of the React tree's churn - see the note in `AppShell` about why the dock
 * must never be inside the router outlet.
 */

/** §6.3's list, plus `track` for a play started on the track's own page. */
export type PlaySource =
  | 'feed'
  | 'profile'
  | 'chart'
  | 'embed'
  | 'search'
  | 'radio'
  | 'collection'
  | 'track'
  | 'dashboard';

export interface PlayableTrack {
  id: number;
  title: string;
  duration_ms: number | null;
  waveform_url: string | null;
  artwork: string | null;
  /** The 200px variant, for the dock's 44px slot. */
  artwork_small?: string | null;
  owner: { username: string; display_name: string } | null;

  /**
   * The artist declared explicit content.
   *
   * Optional for the same reason `favorited` is: a track can reach the queue
   * from somewhere that did not ask for it, and an absent flag means "not
   * stated here" rather than "not explicit".
   */
  explicit?: boolean;

  /*
   * What the server said when this track was handed over, so the dock can draw
   * its heart without a request of its own.
   *
   * Optional because a track can reach the queue from somewhere that did not
   * ask - and because the favourites store's overlay is what wins anyway once
   * anything in this session has changed it. This is the starting point, not
   * the state.
   */
  favorited?: boolean;
  like_count?: number;
}

interface PlayerState {
  track: PlayableTrack | null;
  queue: PlayableTrack[];
  index: number;

  playing: boolean;
  /** Seconds. Updated from `timeupdate`, so roughly four times a second. */
  position: number;
  duration: number;
  volume: number;
  muted: boolean;

  /** True between choosing a track and the first frame being decodable. */
  loading: boolean;
  error: string | null;

  /**
   * Where the audio lives, from the media session response.
   *
   * Not hardcoded, because it differs by environment: a relative `/media` path
   * in development, `https://media.<brand>` in production. Reading it from the
   * server means the client never has to know which.
   */
  mediaBase: string | null;

  /** A monotonic counter the host watches to know a fresh load is wanted. */
  loadToken: number;

  /**
   * Where the current playback started from, for `plays.source` (§6.3).
   *
   * Held on the store rather than passed to the reporting call because the
   * component that knows the answer - a profile list, an album, the charts - is
   * not the one that fires the report thirty seconds later, and by then it may
   * not be mounted.
   */
  source: PlaySource;

  play: (track: PlayableTrack, queue?: PlayableTrack[], source?: PlaySource) => Promise<void>;
  toggle: () => void;
  next: () => void;
  previous: () => void;
  seek: (seconds: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;

  /** Host-only setters. Components should not call these. */
  _sync: (patch: Partial<PlayerState>) => void;
}

const STORAGE_KEY = 'gv.player';

/**
 * What survives a reload.
 *
 * §9.1: "A refresh that loses your place feels broken." Deliberately small and
 * deliberately not a credential - the media token is an httpOnly cookie and is
 * never touched here.
 */
interface Persisted {
  trackId: number | null;
  position: number;
  volume: number;
  muted: boolean;
  queue: PlayableTrack[];
}

function readPersisted(): Partial<Persisted> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    return raw ? (JSON.parse(raw) as Partial<Persisted>) : {};
  } catch {
    // Private mode, disabled storage, or a value from an older shape. None of
    // those should stop the player from working.
    return {};
  }
}

const restored = readPersisted();

export const usePlayer = create<PlayerState>()((set, get) => ({
  track: null,
  queue: restored.queue ?? [],
  index: -1,

  playing: false,
  position: restored.position ?? 0,
  duration: 0,
  volume: restored.volume ?? 1,
  muted: restored.muted ?? false,

  loading: false,
  error: null,
  mediaBase: null,
  loadToken: 0,
  source: 'track',

  play: async (track, queue, source) => {
    const list = queue ?? [track];
    const index = Math.max(0, list.findIndex((t) => t.id === track.id));

    set({
      track,
      queue: list,
      index,
      loading: true,
      error: null,
      position: 0,
      duration: 0,
      // Kept when not given, so advancing through a queue does not relabel
      // every track after the first as coming from somewhere else.
      ...(source ? { source } : {}),
    });

    try {
      /*
       * A media token for the whole queue, minted before anything loads.
       *
       * One token covers every segment of every track in it, which is what lets
       * hls.js fetch segments with no per-URL rewriting - the cookie rides
       * along on its own (§4.4).
       *
       * Capped because the token travels on every segment request and cookies
       * have a practical size limit; a longer queue re-mints as it advances.
       */
      const response = await api.post<{ granted: number[]; media_base: string }>('/media/session', {
        track_ids: list.slice(0, 50).map((t) => t.id),
      });

      set((state) => ({
        mediaBase: response.media_base,
        // Bumped last, so the host sees a complete state before it loads.
        loadToken: state.loadToken + 1,
      }));
    } catch {
      set({ loading: false, playing: false, error: 'Could not start playback.' });
    }
  },

  toggle: () => {
    const { track, playing } = get();

    if (!track) return;

    set({ playing: !playing });
  },

  next: () => {
    const { queue, index } = get();
    const nextTrack = queue[index + 1];

    if (nextTrack) {
      void get().play(nextTrack, queue);
    } else {
      set({ playing: false });
    }
  },

  previous: () => {
    const { queue, index, position } = get();

    /*
     * Restart the track first, then go back.
     *
     * The convention every music player shares: "previous" more than a few
     * seconds in means "start this again", and only a second press goes back.
     * Skipping straight back is the behaviour people complain about.
     */
    if (position > 3) {
      get().seek(0);

      return;
    }

    const previousTrack = queue[index - 1];

    if (previousTrack) {
      void get().play(previousTrack, queue);
    } else {
      get().seek(0);
    }
  },

  seek: (seconds) => set({ position: Math.max(0, seconds), loadToken: get().loadToken }),

  setVolume: (volume) => set({ volume: Math.min(1, Math.max(0, volume)), muted: false }),

  toggleMute: () => set({ muted: !get().muted }),

  _sync: (patch) => set(patch),
}));

/**
 * Persist a little of the state, throttled.
 *
 * Subscribed at module load rather than from an effect, because the player
 * outlives any component and a subscription tied to one would stop the moment
 * that component happened to unmount.
 *
 * Five seconds, per §9.1. Writing on every `timeupdate` would be four
 * synchronous localStorage writes a second for the entire time somebody is
 * listening.
 */
let lastWrite = 0;

usePlayer.subscribe((state) => {
  const now = Date.now();

  if (now - lastWrite < 5000) return;

  lastWrite = now;

  try {
    const payload: Persisted = {
      trackId: state.track?.id ?? null,
      position: state.position,
      volume: state.volume,
      muted: state.muted,
      // Trimmed: the queue is for resuming, and a hundred stored tracks is a
      // large blob written every five seconds for no benefit.
      queue: state.queue.slice(0, 20),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage full or unavailable. Losing the resume point is not worth
    // breaking playback over.
  }
});

/** The track id and offset to restore on boot, if any. */
export function restoredPlayback(): { trackId: number | null; position: number } {
  return { trackId: restored.trackId ?? null, position: restored.position ?? 0 };
}
